// Pure helpers for the composer spell checker (no DOM, no worker).

// Typos that are safe to fix without asking the dictionary.
export const COMMON_TYPOS = {
  teh: "the",
  hte: "the",
  adn: "and",
  taht: "that",
  thier: "their",
  wich: "which",
  whcih: "which",
  becuase: "because",
  becasue: "because",
  recieve: "receive",
  recieved: "received",
  seperate: "separate",
  definately: "definitely",
  occured: "occurred",
  untill: "until",
  wierd: "weird",
  freind: "friend",
  tommorow: "tomorrow",
  tomorow: "tomorrow",
  begining: "beginning",
  accomodate: "accommodate",
  alot: "a lot",
  wnat: "want",
  jsut: "just",
  dont: "don't",
  doesnt: "doesn't",
  didnt: "didn't",
  cant: "can't",
  wont: "won't",
  im: "I'm",
};

const WORD_RE = /[A-Za-zÀ-ɏ]+(?:['’][A-Za-zÀ-ɏ]+)*/g;

export const normalizeWord = (word) =>
  word.replace(/’/g, "'").toLowerCase();

/** [{ word, start, end }] for every alphabetic word in `text`. */
export function tokenize(text) {
  const tokens = [];
  WORD_RE.lastIndex = 0;
  let m;
  while ((m = WORD_RE.exec(text)) !== null) {
    tokens.push({ word: m[0], start: m.index, end: m.index + m[0].length });
  }
  return tokens;
}

function isSentenceStart(text, index) {
  let i = index - 1;
  while (i >= 0 && /[\s"'“‘([]/.test(text[i])) {
    if (text[i] === "\n") return true;
    i--;
  }
  if (i < 0) return true;
  return /[.!?:\n]/.test(text[i]);
}

/**
 * Words we never flag or auto-fix: names, acronyms, URLs, e-mails, codes.
 * School messages are full of student/parent names, so a capitalised word in
 * the middle of a sentence is treated as a proper noun.
 */
export function isIgnorable(text, token) {
  const { word, start, end } = token;
  if (word.length < 2) return true;

  let cs = start;
  let ce = end;
  while (cs > 0 && !/\s/.test(text[cs - 1])) cs--;
  while (ce < text.length && !/\s/.test(text[ce])) ce++;
  const chunk = text.slice(cs, ce);
  if (/[0-9@_\\/#]|:\/\/|www\./i.test(chunk)) return true;
  if (/\.(com|in|org|net|edu|co|gov)\b/i.test(chunk)) return true;

  if (word === word.toUpperCase()) return true; // CBSE, PTA, OK
  if (/[a-z]/.test(word) && /[A-Z]/.test(word.slice(1))) return true; // iPhone

  if (/^[A-Z]/.test(word) && !isSentenceStart(text, start)) return true;
  return false;
}

/** Restore the capitalisation pattern of `source` on `replacement`. */
export function matchCase(source, replacement) {
  if (source[0] && source[0] === source[0].toUpperCase() && /[a-z]/i.test(source[0])) {
    return replacement.charAt(0).toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

/** Optimal-string-alignment distance: a swapped pair of letters costs 1. */
export function editDistance(a, b) {
  const al = a.length;
  const bl = b.length;
  const d = Array.from({ length: al + 1 }, () => new Array(bl + 1).fill(0));
  for (let i = 0; i <= al; i++) d[i][0] = i;
  for (let j = 0; j <= bl; j++) d[0][j] = j;
  for (let i = 1; i <= al; i++) {
    for (let j = 1; j <= bl; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[al][bl];
}

/**
 * The single replacement we are confident enough to apply automatically:
 * exactly one suggestion one edit away, for a plain lowercase word.
 */
export function pickAutoCorrection(word, suggestions) {
  const lower = normalizeWord(word);
  if (lower.length < 4 || /'/.test(lower)) return null;
  const near = suggestions.filter(
    (s) => !/\s/.test(s) && editDistance(lower, normalizeWord(s)) === 1,
  );
  if (near.length !== 1) return null;
  return near[0];
}
